import { CssBaseline, ThemeProvider } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { faIR as pickersFaIR } from '@mui/x-date-pickers/locales';
import { PersianJalaliAdapter } from '@/lib/format/persianJalaliAdapter';
import type { ReactNode } from 'react';
import { darkTheme } from '@/theme/theme';
import { AuthProvider } from '@/features/auth/AuthContext';
import { CurrentProjectProvider } from '@/features/projects/CurrentProjectProvider';
import { ApiError } from '@/lib/api/client';

/**
 * RTL styling.
 *
 * MUI leans on CSS logical properties, but Emotion still emits physical ones for anything
 * written by hand, so the stylis RTL plugin flips them at build time. Without this the app
 * renders as a mirrored left-to-right design, which is exactly what `01-Vision.md` P5 rules
 * out: Persian is meant to be native here, not translated.
 */
const rtlCache = createCache({
  key: 'pc-rtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * 4xx is never retried; 5xx is retried twice.
       *
       * Retrying a 401 fights the refresh logic, and retrying a 404 hammers the server for
       * something that will not appear. Only a server-side failure is worth a second look
       * (`frontend-standards.md` section 9).
       */
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) {
          return false;
        }

        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Never retried. Every mutation in this product writes to GitLab, and a retry of a
      // create is a duplicate issue somebody has to clean up by hand.
      retry: false,
    },
  },
});

export function Providers({ children }: { children: ReactNode }) {
  return (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />

        {/*
          Every calendar in the product is Jalali - principle P5, not a preference. Declaring
          the adapter once here is what makes that true everywhere: a picker mounted without
          it silently falls back to the Gregorian calendar, which is exactly how four native
          date inputs ended up shipping in the wrong calendar.

          Storage stays ISO-8601. The adapter governs what a person reads and picks, not what
          is sent (`08-Non-Functional.md` section 3).
        */}
        <LocalizationProvider
          dateAdapter={PersianJalaliAdapter}
          localeText={pickersFaIR.components.MuiLocalizationProvider.defaultProps.localeText}
        >
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              {/*
                Which project somebody is looking at, shared by every page that has a project
                selector. Inside AuthProvider because it is only meaningful for a signed-in
                person, and above the routes because the whole point is that it survives
                navigation.
              */}
              <CurrentProjectProvider>{children}</CurrentProjectProvider>
            </AuthProvider>
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}
